import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCourseContent, completeLesson, submitModuleEvaluation } from '../../services/learningService';
import { useAuth } from '../../context/AuthContext';
import { 
    BookOpen, CheckCircle, PlayCircle, ChevronLeft, ChevronRight, 
    FileText, Award, Lock, Unlock, AlertCircle, Download
} from 'lucide-react';
import ReactPlayer from 'react-player';

const CoursePlayer = () => {
    const { courseId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeModuleIndex, setActiveModuleIndex] = useState(0);
    const [activeLessonIndex, setActiveLessonIndex] = useState(0);
    const [viewMode, setViewMode] = useState('lesson'); // 'lesson', 'quiz', 'certificate'
    
    // Quiz State
    const [quizAnswers, setQuizAnswers] = useState({});
    const [quizResult, setQuizResult] = useState(null);
    const [isCourseCompleted, setIsCourseCompleted] = useState(false);

    // Use SQL ID (user.id) if available, otherwise fallback to 1 for testing
    const clientId = user?.id || 1;

    useEffect(() => {
        loadCourse();
    }, [courseId]);

    const loadCourse = async () => {
        try {
            const data = await getCourseContent(courseId, clientId);
            setCourse(data);
            
            // Check if course is fully completed
            const allModulesPassed = data.Modulos.every(m => m.Aprobado);
            if (allModulesPassed) {
                setIsCourseCompleted(true);
                setViewMode('certificate');
            }
            
            // Find first incomplete lesson/module to resume
            // Logic to set active indices...
        } catch (error) {
            console.error('Error loading course:', error);
        } finally {
            setLoading(false);
        }
    };

    const currentModule = course?.Modulos[activeModuleIndex];
    const currentLesson = currentModule?.Lecciones[activeLessonIndex];

    // Determine if we should show the result immediately (if already passed)
    const effectiveQuizResult = quizResult || (currentModule?.Aprobado ? { passed: true, score: currentModule.Calificacion } : null);

    const handleLessonComplete = async () => {
        try {
            await completeLesson(currentLesson.LeccionId, clientId);
            // Update local state to show completed
            const updatedCourse = { ...course };
            updatedCourse.Modulos[activeModuleIndex].Lecciones[activeLessonIndex].Completada = 1;
            setCourse(updatedCourse);
            
            // Move to next lesson or quiz
            if (activeLessonIndex < currentModule.Lecciones.length - 1) {
                setActiveLessonIndex(activeLessonIndex + 1);
            } else {
                setViewMode('quiz');
            }
        } catch (error) {
            alert('Error al completar lección');
        }
    };

    const handleQuizSubmit = async () => {
        try {
            const result = await submitModuleEvaluation(currentModule.ModuloId, clientId, quizAnswers);
            setQuizResult(result);
            if (result.passed) {
                // Update local state
                const updatedCourse = { ...course };
                updatedCourse.Modulos[activeModuleIndex].Aprobado = 1;
                setCourse(updatedCourse);
                
                // Check if this was the last module
                const isLastModule = activeModuleIndex === course.Modulos.length - 1;
                if (isLastModule) {
                    setIsCourseCompleted(true);
                }
            }
        } catch (error) {
            alert('Error al enviar evaluación');
        }
    };

    const nextModule = () => {
        if (activeModuleIndex < course.Modulos.length - 1) {
            setActiveModuleIndex(activeModuleIndex + 1);
            setActiveLessonIndex(0);
            setViewMode('lesson');
            setQuizResult(null);
            setQuizAnswers({});
        } else {
            // Course Finished
            setViewMode('certificate');
        }
    };

    if (loading) return <div className="flex items-center justify-center h-screen text-slate-500">Cargando curso...</div>;
    if (!course) return <div className="flex items-center justify-center h-screen text-slate-500">Curso no encontrado</div>;

    return (
        <div className="flex h-screen bg-slate-50 overflow-hidden">
            {/* Sidebar Navigation */}
            <aside className="w-80 bg-white border-r border-slate-200 flex flex-col shrink-0 z-20">
                <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                    <button onClick={() => navigate('/campus')} className="p-2 hover:bg-slate-100 rounded-full text-slate-500">
                        <ChevronLeft size={20} />
                    </button>
                    <h1 className="font-bold text-slate-800 truncate" title={course.Curso}>{course.Curso}</h1>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                    {course.Modulos.map((modulo, mIndex) => (
                        <div key={modulo.ModuloId} className={`relative pl-4 border-l-2 ${mIndex === activeModuleIndex ? 'border-blue-600' : 'border-slate-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className={`font-bold text-sm ${mIndex === activeModuleIndex ? 'text-blue-600' : 'text-slate-700'}`}>
                                    {modulo.Modulo}
                                </h3>
                                {modulo.Aprobado ? <CheckCircle size={16} className="text-green-500" /> : 
                                 (mIndex > 0 && !course.Modulos[mIndex-1].Aprobado) ? <Lock size={14} className="text-slate-300" /> : null}
                            </div>
                            
                            <div className="space-y-1">
                                {modulo.Lecciones.map((leccion, lIndex) => (
                                    <button
                                        key={leccion.LeccionId}
                                        disabled={mIndex > 0 && !course.Modulos[mIndex-1].Aprobado} // Lock if prev module not passed
                                        onClick={() => {
                                            setActiveModuleIndex(mIndex);
                                            setActiveLessonIndex(lIndex);
                                            setViewMode('lesson');
                                        }}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center gap-2 transition-colors ${
                                            activeModuleIndex === mIndex && activeLessonIndex === lIndex && viewMode === 'lesson'
                                            ? 'bg-blue-50 text-blue-700 font-medium'
                                            : 'text-slate-500 hover:bg-slate-50'
                                        }`}
                                    >
                                        {leccion.Completada ? <CheckCircle size={12} className="text-green-500" /> : <div className="w-3 h-3 rounded-full border border-slate-300"></div>}
                                        <span className="truncate">{leccion.Leccion}</span>
                                    </button>
                                ))}
                                
                                <button
                                    disabled={mIndex > 0 && !course.Modulos[mIndex-1].Aprobado}
                                    onClick={() => {
                                        setActiveModuleIndex(mIndex);
                                        setViewMode('quiz');
                                    }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center gap-2 transition-colors ${
                                        activeModuleIndex === mIndex && viewMode === 'quiz'
                                        ? 'bg-purple-50 text-purple-700 font-medium'
                                        : 'text-slate-500 hover:bg-slate-50'
                                    }`}
                                >
                                    <Award size={12} className={modulo.Aprobado ? "text-green-500" : "text-purple-500"} />
                                    <span>Evaluación del Módulo</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col h-full overflow-hidden relative">
                {viewMode === 'lesson' && currentLesson ? (
                    <div className="flex-1 overflow-y-auto">
                        {/* Video Section */}
                        {currentLesson.VideoURL && (
                            <div className="w-full bg-black aspect-video">
                                <ReactPlayer 
                                    url={currentLesson.VideoURL} 
                                    width="100%" 
                                    height="100%" 
                                    controls 
                                />
                            </div>
                        )}

                        {/* Content Section */}
                        <div className="max-w-3xl mx-auto p-8 pb-24">
                            <div className="mb-6">
                                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2 block">
                                    Módulo {activeModuleIndex + 1}: {currentModule.Modulo}
                                </span>
                                <h1 className="text-3xl font-bold text-slate-900">{currentLesson.Leccion}</h1>
                            </div>

                            <div className="prose prose-slate max-w-none mb-12" dangerouslySetInnerHTML={{ __html: currentLesson.Contenido }} />

                            <div className="flex justify-end">
                                <button 
                                    onClick={handleLessonComplete}
                                    className={`px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-all ${
                                        currentLesson.Completada 
                                        ? 'bg-green-100 text-green-700 cursor-default' 
                                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20'
                                    }`}
                                >
                                    {currentLesson.Completada ? (
                                        <>
                                            <CheckCircle size={20} /> Completada
                                        </>
                                    ) : (
                                        <>
                                            Marcar como Completada <ChevronRight size={20} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : viewMode === 'certificate' ? (
                    <div className="flex-1 overflow-y-auto bg-slate-50 p-8 flex items-center justify-center">
                        <style>
                            {`
                                @media print {
                                    body * {
                                        visibility: hidden;
                                    }
                                    #printable-certificate, #printable-certificate * {
                                        visibility: visible;
                                        -webkit-print-color-adjust: exact !important;
                                        print-color-adjust: exact !important;
                                    }
                                    #printable-certificate {
                                        position: fixed;
                                        left: 0;
                                        top: 0;
                                        width: 100%;
                                        height: 100%;
                                        margin: 0;
                                        padding: 0;
                                        border: none !important;
                                        box-shadow: none !important;
                                        z-index: 9999;
                                        background: white;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                    }
                                    @page {
                                        size: landscape;
                                        margin: 0;
                                    }
                                }
                            `}
                        </style>
                        <div className="max-w-4xl w-full space-y-8">
                            {/* Certificate Card */}
                            <div id="printable-certificate" className="bg-white rounded-2xl shadow-2xl p-12 text-center border-8 border-double border-slate-100 relative overflow-hidden">
                                {/* Decorative Background Elements */}
                                <div className="absolute top-0 left-0 w-32 h-32 bg-blue-50 rounded-br-full opacity-50"></div>
                                <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-50 rounded-tl-full opacity-50"></div>
                                
                                <div className="relative z-10 space-y-8 w-full">
                                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full flex items-center justify-center mx-auto shadow-lg">
                                        <Award size={48} />
                                    </div>
                                    
                                    <div className="space-y-6">
                                        <div className="space-y-2">
                                            <h2 className="text-5xl font-serif font-bold text-slate-900 tracking-tight">Certificado de Excelencia</h2>
                                            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-600 mx-auto rounded-full"></div>
                                            <p className="text-slate-500 uppercase tracking-[0.2em] text-sm pt-4">Se otorga el presente reconocimiento a</p>
                                        </div>

                                        <div className="py-8 relative">
                                            <div className="absolute inset-0 flex items-center justify-center opacity-5">
                                                <Award size={200} />
                                            </div>
                                            <h3 className="text-4xl md:text-5xl font-serif font-bold text-blue-700 relative z-10 italic">
                                                {user?.displayName || user?.name || 'Estudiante'}
                                            </h3>
                                            <div className="w-64 h-px bg-slate-200 mx-auto mt-4"></div>
                                        </div>

                                        <div className="space-y-4">
                                            <p className="text-slate-600 text-lg">Por haber completado y aprobado satisfactoriamente el curso profesional de</p>
                                            <h4 className="text-3xl font-bold text-slate-800 max-w-3xl mx-auto leading-tight">{course?.Curso}</h4>
                                            <p className="text-slate-500">Demostrando compromiso, dedicación y dominio de los contenidos impartidos.</p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-12 pt-12 max-w-2xl mx-auto">
                                            <div className="text-center space-y-2">
                                                <div className="border-b border-slate-300 pb-2 mb-2">
                                                    <p className="font-serif text-xl text-slate-800 italic">X Project S.A.</p>
                                                </div>
                                                <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Institución</p>
                                            </div>
                                            <div className="text-center space-y-2">
                                                <div className="border-b border-slate-300 pb-2 mb-2">
                                                    <p className="font-serif text-xl text-slate-800 italic">{new Date().toLocaleDateString()}</p>
                                                </div>
                                                <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Fecha de Emisión</p>
                                            </div>
                                        </div>
                                        
                                        <div className="pt-8 opacity-60">
                                            <p className="text-[10px] text-slate-400 uppercase tracking-widest">ID de Credencial: XP-{course?.CursoId}-{user?.id || '000'}-{Date.now().toString().slice(-6)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-center gap-4 print:hidden">
                                <button 
                                    onClick={() => window.print()}
                                    className="px-6 py-3 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 shadow-xl flex items-center gap-2 transition-transform hover:scale-105"
                                >
                                    <Download size={20} /> Descargar PDF
                                </button>
                                <button 
                                    onClick={() => {
                                        setViewMode('lesson');
                                        setActiveModuleIndex(0);
                                        setActiveLessonIndex(0);
                                    }}
                                    className="px-6 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-lg flex items-center gap-2"
                                >
                                    <BookOpen size={20} /> Repasar Contenido
                                </button>
                                <button 
                                    onClick={() => navigate('/campus')}
                                    className="px-6 py-3 bg-white text-slate-700 font-bold rounded-lg hover:bg-slate-50 border border-slate-200 shadow-sm"
                                >
                                    Volver al Campus
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto bg-slate-50 p-8 flex items-center justify-center">
                        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Award size={32} />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">Evaluación: {currentModule.Modulo}</h2>
                                <p className="text-slate-500">Responde correctamente para desbloquear el siguiente módulo.</p>
                            </div>

                            {!effectiveQuizResult ? (
                                <div className="space-y-8">
                                    {currentModule.Preguntas.map((pregunta, idx) => (
                                        <div key={pregunta.PreguntaId} className="space-y-3">
                                            <p className="font-medium text-slate-800">{idx + 1}. {pregunta.Pregunta}</p>
                                            <div className="space-y-2 pl-4">
                                                {pregunta.Opciones.map(opcion => (
                                                    <label key={opcion.OpcionId} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                                                        <input 
                                                            type="radio" 
                                                            name={`q-${pregunta.PreguntaId}`}
                                                            className="w-4 h-4 text-blue-600"
                                                            onChange={() => setQuizAnswers({...quizAnswers, [pregunta.PreguntaId]: opcion.OpcionId})}
                                                        />
                                                        <span className="text-sm text-slate-600">{opcion.Opcion}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    <button 
                                        onClick={handleQuizSubmit}
                                        disabled={Object.keys(quizAnswers).length < currentModule.Preguntas.length}
                                        className="w-full py-3 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Enviar Respuestas
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center space-y-6 animate-in fade-in zoom-in">
                                    {effectiveQuizResult.passed ? (
                                        <>
                                            <div className="text-green-500 text-5xl font-bold mb-2">{Math.round(effectiveQuizResult.score)}%</div>
                                            <h3 className="text-xl font-bold text-slate-800">¡Felicitaciones!</h3>
                                            <p className="text-slate-600">Has aprobado este módulo correctamente.</p>
                                            <button 
                                                onClick={nextModule}
                                                className="px-8 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-lg shadow-blue-600/20"
                                            >
                                                Continuar al Siguiente Módulo
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-red-500 text-5xl font-bold mb-2">{Math.round(effectiveQuizResult.score)}%</div>
                                            <h3 className="text-xl font-bold text-slate-800">Inténtalo de nuevo</h3>
                                            <p className="text-slate-600">Necesitas un 70% para aprobar. Revisa el contenido y vuelve a intentar.</p>
                                            <button 
                                                onClick={() => { setQuizResult(null); setQuizAnswers({}); }}
                                                className="px-8 py-3 bg-slate-100 text-slate-700 font-bold rounded-lg hover:bg-slate-200"
                                            >
                                                Reintentar
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default CoursePlayer;
